import * as React from "react";
import { useState } from "react";
import { generateDynamicColumns } from "../../utils/generateDynamicColumns";
import { columnHelper, fileToBase64 } from "../../utils/utils";
import TableTemplate from "../../components/common/TableTemplate";
import LoadingState from "../../components/states/LoadingState";
import {
  useGetAllAdSetsQuery,
  useUpdateAdSetMutation,
} from "../../services/ad";
import { createFieldUpdater } from "../../utils/createFieldUpdater";
import { useSelector } from "react-redux";
import { schemas, validateAndToast } from "../../utils/validation";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/button";
import ErrorState from "../../components/states/ErrorState";
import { PlusIcon } from "lucide-react";
import { useDispatch } from "react-redux";
import { setErrorFields } from "../../store/tableSlice";

const AdsetLauncherPage = () => {
  const { data: adSets, isLoading, isError, error } = useGetAllAdSetsQuery();
  const [updateAdSet] = useUpdateAdSetMutation();
  const selectedRows = useSelector((state) => state.table.selectedRows);

  const [uploadingFiles, setUploadingFiles] = useState({});
  const [duplicatingRows, setDuplicatingRows] = useState({});
  const [launchingRows, setLaunchingRows] = useState({});
  const [deletingRows, setDeletingRows] = useState({});

  const dispatch = useDispatch();

  const columns = React.useMemo(() => {
    return generateDynamicColumns({
      columnHelper,
      headerData: adSets?.[0]?.Column_header,
      columnTypes: {
        rowSelectionColumn: adSets?.[0]?.Column_header.row_checkbox,
        actionsColumn: adSets?.[0]?.Column_header.row_actions,
      },
      enableSubrows: true,
      actionConfig: {
        launch: {
          onClick: async (row) => {
            // Use selectedRows if any are selected, otherwise use just the clicked row
            const rowsToLaunch = selectedRows.length > 0 ? selectedRows : [row];

            // Check for missing required fields for each row to launch
            const rowsWithMissingFields = [];

            for (const rowItem of rowsToLaunch) {
              const rowData = rowItem.original;
              const missingFields = Object.entries(rowData)
                .filter(([field, fieldData]) => {
                  // Skip if fieldData is null/undefined or doesn't have required attribute
                  if (!fieldData || typeof fieldData !== "object") return false;

                  // Check if field is required
                  if (!fieldData.required) return false;

                  // Handle fields with selected/values structure (dropdowns)
                  if ("selected" in fieldData) {
                    return (
                      !fieldData.selected ||
                      (Array.isArray(fieldData.selected) &&
                        fieldData.selected.length === 0)
                    );
                  }

                  if ("files" in fieldData && Array.isArray(fieldData.files)) {
                    return fieldData.files.length === 0;
                  }

                  // Handle fields with direct display value
                  if ("display" in fieldData) {
                    return (
                      !fieldData.display || fieldData.display.trim() === ""
                    );
                  }

                  // Handle fields with values array (like files)
                  if (
                    "values" in fieldData &&
                    Array.isArray(fieldData.values)
                  ) {
                    return fieldData.values.length === 0;
                  }

                  return true; // Field is missing entirely or in invalid format
                })
                .map(([field]) => field);

              if (missingFields.length > 0) {
                rowsWithMissingFields.push({
                  rowId: rowItem.id,
                  fields: missingFields,
                });
              }
            }

            // Filter out ads that are already launched
            const unlaunchedAdsets = rowsToLaunch.filter((rowItem) => {
              const status = rowItem.original.status?.display || "";
              return status !== "launched";
            });

            // Check if there are any adsets left to launch
            if (unlaunchedAdsets.length === 0) {
              toast.error("All selected adsets are already launched");
              return;
            }

            setLaunchingRows((prev) => ({ ...prev, [row.id]: true }));
            try {
              const payload = [
                {
                  action: "launch_facebook_ad_set",
                  ad_glids: unlaunchedAdsets.map(
                    (rowItem) => rowItem.original.glid.display
                  ),
                },
              ];
              const result = await updateAdSet(payload);
              if (result.error) {
                console.log("error: ", result.error);

                // Extract error messages from the API response
                let errorMessage = "Something went wrong";

                if (result?.error?.data && Array.isArray(result.error.data)) {
                  // Get all error messages from the array
                  const errorMessages = result.error.data
                    .filter((item) => !item.success && item.message)
                    .map((item) => `${item.ad_glid}: ${item.message}`);

                  if (errorMessages.length > 0) {
                    errorMessage =
                      errorMessages.length === 1
                        ? errorMessages[0]
                        : errorMessages.join(", ");
                  }
                }

                toast.error(errorMessage);
              } else {
                const successMessage =
                  unlaunchedAdsets.length === 1
                    ? "Adset launched successfully"
                    : `${unlaunchedAdsets.length} adsets launched successfully`;
                toast.success(successMessage);
              }
            } finally {
              setLaunchingRows((prev) => ({}));
            }
          },
          isLoading: (rowId) => {
            const isLoading = launchingRows[rowId];
            return isLoading;
          },
        },
        duplicate: {
          onClick: async (row) => {
            const rowId = row.id;
            const rowOriginal = row.original;
            try {
              setDuplicatingRows((prev) => ({ ...prev, [rowId]: true }));
              const payload = [
                {
                  action: "duplicate_object",
                  object_glid: rowOriginal.glid.display,
                  object_class: "AdSetProject",
                },
              ];
              const result = await updateAdSet(payload);
              if (result.error) {
                const errorMessage =
                  result?.error?.data?.[0]?.response?.error?.message ||
                  result?.error?.data?.[0] ||
                  "Something went wrong";
                toast.error(errorMessage);
              } else {
                toast.success("Adset duplicated successfully");
              }
            } finally {
              setDuplicatingRows((prev) => ({}));
            }
          },
          isLoading: (rowId) => {
            const isLoading = duplicatingRows[rowId];
            return isLoading;
          },
        },
        delete: {
          onClick: async (row) => {
            const rowId = row.id;
            const rowOriginal = row.original;
            try {
              setDeletingRows((prev) => ({ ...prev, [rowId]: true }));
              const payload = [
                {
                  action: "delete_object",
                  object_glids: rowOriginal.glid.display,
                  object_class: "AdSetProject",
                },
              ];
              const result = await updateAdSet(payload);
              if (result.error) {
                const errorMessage =
                  result?.error?.data?.[0]?.response?.error?.message ||
                  result?.error?.data?.[0] ||
                  "Something went wrong";
                toast.error(errorMessage);
              } else {
                toast.success("Adset deleted successfully");
              }
            } finally {
              setDeletingRows((prev) => ({}));
            }
          },
          isLoading: (rowId) => deletingRows[rowId],
        },
      },
      editableFields: {
        name: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "name",
            selectedRows,
            updateAdSet
          ),
          placeholder: "Enter Adset Name",
        },
        ad_account_id: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "ad_account_id",
            selectedRows,
            updateAdSet
          ),
        },
        campaign_id: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "campaign_id",
            selectedRows,
            updateAdSet
          ),
        },
        dest_url: {
          inputType: "url",
          placeholder: "Enter destination URL",
          onSave: async (columnInfo, row, editedValue) => {
            // we can add this validation in the createFieldUpdate function as well
            if (!(await validateAndToast(editedValue, schemas.url))) {
              return false;
            }
            return await createFieldUpdater(
              "AdSetProject",
              "dest_url",
              selectedRows,
              updateAdSet
            )(columnInfo, row, editedValue);
          },
        },
        headline: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "headline",
            selectedRows,
            updateAdSet
          ),
        },
        primary_text: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "primary_text",
            selectedRows,
            updateAdSet
          ),
        },
        description: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "description",
            selectedRows,
            updateAdSet
          ),
        },
        page_id: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "page_id",
            selectedRows,
            updateAdSet
          ),
        },
        instagram_id: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "instagram_id",
            selectedRows,
            updateAdSet
          ),
        },
        cta: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "cta",
            selectedRows,
            updateAdSet
          ),
        },
        gender: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "gender",
            selectedRows,
            updateAdSet
          ),
        },
        language: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "language",
            selectedRows,
            updateAdSet
          ),
        },
        saved_audience: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "saved_audience",
            selectedRows,
            updateAdSet
          ),
        },
        country: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "country",
            selectedRows,
            updateAdSet
          ),
        },
        excluded_audience_id: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "excluded_audience_id",
            selectedRows,
            updateAdSet
          ),
        },
        interest: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "interest",
            selectedRows,
            updateAdSet
          ),
        },
        pixel_id: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "pixel_id",
            selectedRows,
            updateAdSet
          ),
        },
        daily_budget: {
          inputProps: {
            type: "number",
          },
          onSave: createFieldUpdater(
            "AdSetProject",
            "daily_budget",
            selectedRows,
            updateAdSet
          ),
        },
        forecasted_launch_date: {
          onSave: createFieldUpdater(
            "AdSetProject",
            "forecasted_launch_date",
            selectedRows,
            updateAdSet
          ),
        },
        z_files: {
          onSave: async (columnInfo, row, files) => {
            let payload = [];

            // Case 1 & 2: Single row selected (with one or multiple files)
            if (selectedRows.length < 1 || !row.getIsSelected()) {
              const originalRow = row.original;
              const rowId = row.id;
              setUploadingFiles((prev) => ({ ...prev, [rowId]: true }));

              try {
                // Process each file for this single row
                for (const file of files) {
                  const base64 = await fileToBase64(file);
                  payload.push({
                    action: "upload_file_content",
                    file_content: base64,
                    file_name: file.name,
                    object_glid: originalRow.glid.display,
                    object_class: "AdSetProject",
                  });
                }

                const result = await updateAdSet(payload);
                if (result.error) {
                  const errorMessage =
                    result?.error?.data?.[0]?.response?.error?.message || // from your JSON structure
                    result?.error?.data?.[0] ||
                    "Something went wrong";
                  toast.error(errorMessage);
                } else {
                  toast.success("Files uploaded successfully");
                }
              } finally {
                setUploadingFiles((prev) => ({}));
              }
              return;
            }

            // Case 3 & 4: Multiple rows selected (with one or multiple files)
            else if (selectedRows.length >= 1) {
              // For each combination of selected row and file, create an entry in the payload
              for (const file of files) {
                // First convert file to base64 once to avoid redundant processing
                const base64 = await fileToBase64(file);

                // For each row, create a payload entry with this file
                for (const selectedRow of selectedRows) {
                  payload.push({
                    action: "upload_file_content",
                    file_content: base64,
                    file_name: file.name,
                    object_glid: selectedRow.original.glid.display,
                    object_class: "AdSetProject",
                  });
                }
              }

              // Now we have a payload array with entries for each row-file combination
              try {
                setUploadingFiles((prev) =>
                  selectedRows.reduce(
                    (acc, row) => ({ ...acc, [row.id]: true }),
                    prev
                  )
                );

                const result = await updateAdSet(payload);
                if (result.error) {
                  const errorMessage =
                    result?.error?.data?.[0]?.response?.error?.message || // from your JSON structure
                    result?.error?.data?.[0] ||
                    "Something went wrong";
                  toast.error(errorMessage);
                } else {
                  toast.success(
                    `Successfully uploaded ${payload.length} files`
                  );
                }
              } finally {
                setUploadingFiles((prev) => ({}));
              }
              return;
            }
          },
          isLoading: (rowId) => uploadingFiles[rowId],
        },
      },
      filterConfig: {
        filterTypes: {
          status: "select",
        },
      },
      navigationConfig: {
        dest_url: {
          navigable: true,
          getUrl: ({ originalRow, columnInfo }) => {
            return {
              pathname: originalRow?.[columnInfo.key]?.display,
              state: null,
            };
          },
        },
      },
    });
  }, [
    adSets,
    selectedRows,
    updateAdSet,
    uploadingFiles,
    duplicatingRows,
    launchingRows,
    deletingRows,
    dispatch,
  ]);

  const subrowColumns = React.useMemo(() => {
    return generateDynamicColumns({
      columnHelper,
      headerData: adSets?.[0]?.Column_header.subrows,
      columnTypes: {
        rowSelectionColumn: adSets?.[0]?.Column_header.subrows.row_checkbox,
      },
      enableSubrows: true,
      editableFields: {
        ...Object.fromEntries(
          Object.keys(adSets?.[0]?.Column_header?.subrows || {}).map(
            (fieldName) => [
              fieldName,
              {
                onSave: createFieldUpdater(
                  "AdSetProjectAd",
                  fieldName,
                  selectedRows,
                  updateAdSet
                ),
              },
            ]
          )
        ),
        z_files: {
          onSave: async (columnInfo, row, files) => {
            let payload = [];

            // Case 1 & 2: Single row selected (with one or multiple files)
            if (selectedRows.length < 1 || !row.getIsSelected()) {
              const originalRow = row.original;
              const rowId = row.id;
              setUploadingFiles((prev) => ({ ...prev, [rowId]: true }));

              try {
                // Process each file for this single row
                for (const file of files) {
                  const base64 = await fileToBase64(file);
                  payload.push({
                    action: "upload_file_content",
                    file_content: base64,
                    file_name: file.name,
                    object_glid: originalRow.glid.display,
                    object_class: "AdSetProject",
                  });
                }

                const result = await updateAdSet(payload);
                if (result.error) {
                  const errorMessage =
                    result?.error?.data?.[0]?.response?.error?.message || // from your JSON structure
                    result?.error?.data?.[0] ||
                    "Something went wrong";
                  toast.error(errorMessage);
                } else {
                  toast.success("Files uploaded successfully");
                }
              } finally {
                setUploadingFiles((prev) => ({}));
              }
              return;
            }

            // Case 3 & 4: Multiple rows selected (with one or multiple files)
            else if (selectedRows.length >= 1) {
              // For each combination of selected row and file, create an entry in the payload
              for (const file of files) {
                // First convert file to base64 once to avoid redundant processing
                const base64 = await fileToBase64(file);

                // For each row, create a payload entry with this file
                for (const selectedRow of selectedRows) {
                  payload.push({
                    action: "upload_file_content",
                    file_content: base64,
                    file_name: file.name,
                    object_glid: selectedRow.original.glid.display,
                    object_class: "AdSetProject",
                  });
                }
              }

              // Now we have a payload array with entries for each row-file combination
              try {
                setUploadingFiles((prev) =>
                  selectedRows.reduce(
                    (acc, row) => ({ ...acc, [row.id]: true }),
                    prev
                  )
                );

                const result = await updateAdSet(payload);
                if (result.error) {
                  const errorMessage =
                    result?.error?.data?.[0]?.response?.error?.message || // from your JSON structure
                    result?.error?.data?.[0] ||
                    "Something went wrong";
                  toast.error(errorMessage);
                } else {
                  toast.success(
                    `Successfully uploaded ${payload.length} files`
                  );
                }
              } finally {
                setUploadingFiles((prev) => ({}));
              }
              return;
            }
          },
          isLoading: (rowId) => uploadingFiles[rowId],
        },
      },
    });
  }, [adSets, selectedRows, updateAdSet, uploadingFiles]);

  const handleCreateNewAd = async () => {
    const payload = [
      {
        class: "AdSetProject",
        glid: null,
        update: { name: "New Adset Project" },
      },
    ];
    const result = await updateAdSet(payload);
    if (result.error) {
      const errorMessage =
        result?.error?.data?.[0]?.response?.error?.message ||
        result?.error?.data?.[0] ||
        "Something went wrong";
      toast.error(errorMessage);
    } else {
      toast.success("New adset created successfully");
    }
  };

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return <ErrorState error={error} />;
  }

  return (
    <div className="space-y-4 p-1">
      <div style={{ backgroundColor: adSets?.[0]?.Color }} className="p-2">
        <h1 className="text-2xl font-bold text-gray-800">
          {adSets?.[0]?.title || "Adset Launcher Page"}
        </h1>
        <h2 className="text-lg font-medium text-gray-800/75">
          {adSets?.[0]?.["sub-title"] || "Adset Launcher Page description"}
        </h2>
      </div>
      <div>
        <Button
          variant="ghost"
          size="default"
          className="outline outline-1"
          onClick={handleCreateNewAd}
        >
          <PlusIcon className="w-4 h-4" />
          <span>New Ad Set</span>
        </Button>
      </div>
      <TableTemplate
        subrowColumns={subrowColumns}
        columns={columns}
        data={adSets?.[0]?.data}
        options={{
          enableSubrows: true,
          enablePagination: true,
          enableRowSelection: true,
          defaultPageSize: 10,
        }}
      />
    </div>
  );
};

export default AdsetLauncherPage;
